import { useHistory } from 'react-router-dom';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import {
    faBadgeDollar,
    faQuestionCircle,
    faSparkles,
    faTag,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickableLineItem } from 'assets/js/pages/transactions/ClickableLineItem';
import * as TicketLineItem from 'assets/js/pages/transactions/TicketLineItem';
import { getProductIcon } from 'assets/js/pages/transactions/helpers';
import * as Tooltip from 'components/tooltip/Tooltip';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { useTaxDeductibleSettings } from 'customHooks/useTaxDeductibleSettings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twJoin } from 'tailwind-merge';

const TransactionLineItems = () => {
    const history = useHistory();
    const { accountId } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const { data: transaction } = useCurrentTransactionOrById();
    const lineItemsRaw = transaction?.transactions[0].line_items ?? [];
    const possibleGeneratedTickets = transaction.tickets ?? [];
    const { isVisible: isTaxDeductibleVisible } = useTaxDeductibleSettings();
    const { taxDeductibilitySettings = false } = useFlags();

    const lineItems = lineItemsRaw.filter(item => {
        const stillGeneratingTickets =
            item.subtype === 'ticket' && possibleGeneratedTickets.length > 0;

        return (
            item.subtype !== 'tip' &&
            item.subtype !== 'fee' &&
            item.subtype !== 'shipping' &&
            !stillGeneratingTickets
        );
    });

    const donorPledgeCoveredFee = pickOrZero(transaction.fee_covered);
    const transactionFee = pickOrZero(transaction.fee);
    const payoutCost = pickOrZero(transaction.payout_cost);
    const shippingCost = pickOrZero(transaction.shipping);
    const payout = pickOrZero(transaction.payout);
    const total = pickOrZero(transaction.total_display);
    const subtotal = pickOrZero(transaction.subtotal);
    const taxDeductibleAmount = pickOrZero(transaction.tax_deductible_amount);

    const didCoverEntireFee =
        donorPledgeCoveredFee > 0 && transactionFee > 0 && donorPledgeCoveredFee === transactionFee;
    const showProcessingFee = !didCoverEntireFee && transactionFee > 0;

    return (
        <div className={`flex flex-col gap-4 rounded-xl bg-gray-50 p-4`}>
            {lineItems.map(lineItem => {
                const backgroundImage =
                    lineItem.item?.pictures && lineItem.item.pictures.length > 0
                        ? lineItem.item.pictures[0].url
                        : undefined;
                const isAuctionItem = !!lineItem.item && !!lineItem.item?.auction_id;

                const goToAuctionItem = () => {
                    history.push(
                        `/accounts/${accountId}/campaigns/${transaction.campaign.id}/auction/items/${lineItem.item?.id}`,
                    );
                };

                return (
                    <ClickableLineItem
                        key={lineItem.id}
                        onClick={isAuctionItem ? () => goToAuctionItem() : undefined}
                    >
                        <div
                            className={
                                'flex aspect-square h-12 items-center justify-center rounded bg-gray-200 bg-cover bg-center bg-no-repeat'
                            }
                            style={
                                backgroundImage
                                    ? { backgroundImage: `url(${backgroundImage})` }
                                    : {}
                            }
                        >
                            {!backgroundImage && (
                                <FontAwesomeIcon
                                    className={'text-gray-600'}
                                    size={'lg'}
                                    icon={getProductIcon(lineItem)}
                                />
                            )}
                        </div>
                        <div className={'flex flex-grow gap-4'}>
                            <div className={'flex flex-grow flex-col'}>
                                <p
                                    className={
                                        'whitespace-pre-wrap text-sm font-medium text-gray-800'
                                    }
                                >
                                    {lineItem.description}{' '}
                                    {lineItem.quantity > 1 ? `(x${lineItem.quantity})` : ''}
                                </p>
                                {lineItem?.item && !!lineItem?.item.auction_id && (
                                    <p className={'text-sm text-gray-600'}>
                                        Item ID #{lineItem.item.id}
                                    </p>
                                )}
                            </div>
                            <div className={'flex flex-col items-end'}>
                                <p className={'text-sm font-medium text-gray-600'}>
                                    {currencySymbol}
                                    {maskDecimal(lineItem.total)}
                                </p>
                                {taxDeductibilitySettings && lineItem.fair_market_value_amount && (
                                    <p className={'text-sm italic text-gray-500'}>
                                        (Fair market value: {currencySymbol}
                                        {maskDecimal(lineItem.fair_market_value_amount)})
                                    </p>
                                )}

                                {isAuctionItem && <p className={'text-sm text-blue-500'}>View</p>}
                            </div>
                        </div>
                    </ClickableLineItem>
                );
            })}
            {possibleGeneratedTickets.map(ticket => (
                <TicketLineItem.Root key={ticket.id} ticket={ticket}>
                    <TicketLineItem.Icon />
                    <TicketLineItem.DetailsContainer>
                        <TicketLineItem.Details />
                        <TicketLineItem.Price />
                    </TicketLineItem.DetailsContainer>
                </TicketLineItem.Root>
            ))}
            <Separator className={'bg-gray-200'} />
            <div className={'flex flex-col'}>
                <div className={'flex items-center justify-between gap-4'}>
                    <p className={'text-sm font-medium text-gray-800'}>Subtotal</p>
                    <p className={'text-sm font-medium text-gray-600'}>
                        {currencySymbol}
                        {maskDecimal(subtotal)}
                    </p>
                </div>
                {transaction.discount && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <div className={'flex items-center gap-4'}>
                            <p className={'text-sm font-medium text-gray-800'}>Promo code</p>
                            <div className={'flex items-center gap-1'}>
                                <FontAwesomeIcon
                                    className={'text-gray-600'}
                                    size={'sm'}
                                    icon={faTag}
                                />
                                <p className={'text-sm text-gray-600'}>
                                    {transaction.discount.code}
                                </p>
                            </div>
                        </div>
                        <p className={'text-sm font-medium text-gray-600'}>
                            -{currencySymbol}
                            {maskDecimal(transaction.discount.amount)}
                        </p>
                    </div>
                )}
                {shippingCost > 0 && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <p className={'text-sm font-medium text-gray-800'}>Shipping</p>
                        <p className={'text-sm font-medium text-gray-600'}>
                            {currencySymbol}
                            {maskDecimal(shippingCost)}
                        </p>
                    </div>
                )}
                {donorPledgeCoveredFee > 0 && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <div className={'flex items-center gap-2'}>
                            <p className={'text-sm font-medium'}>Donor covered fee</p>
                        </div>
                        <p className={twJoin('text-sm font-medium text-gray-600')}>
                            {currencySymbol}
                            {maskDecimal(donorPledgeCoveredFee)}
                        </p>
                    </div>
                )}
            </div>
            <Separator className={'bg-gray-200'} />
            <div className={'flex flex-col'}>
                <div className={'flex items-center justify-between gap-4'}>
                    <p className={'font-medium'}>Total</p>
                    <p className={'font-medium'}>
                        {currencySymbol}
                        {maskDecimal(total)}
                    </p>
                </div>
                {showProcessingFee && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <div className={'flex items-center gap-2'}>
                            <p className={'text-sm font-medium'}>Processing fee</p>
                        </div>
                        <p className={twJoin('text-sm font-medium text-gray-600')}>
                            -{currencySymbol}
                            {maskDecimal(transactionFee)}
                        </p>
                    </div>
                )}
                {payoutCost > 0 && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <div className={'flex items-center gap-2'}>
                            <p className={'text-sm font-medium'}>Item cost</p>
                        </div>
                        <p className={twJoin('text-sm font-medium text-gray-600')}>
                            -{currencySymbol}
                            {maskDecimal(payoutCost)}
                        </p>
                    </div>
                )}
            </div>
            <Separator className={'bg-gray-200'} />
            <div className={'flex flex-col'}>
                <div className={'flex items-center justify-between gap-4'}>
                    <div className={'flex items-center gap-1'}>
                        <p className={'text-brand-violet-primary text-sm font-medium'}>
                            Net payout
                        </p>
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <span className="sr-only">More info</span>
                                <FontAwesomeIcon
                                    className={'text-brand-violet-primary'}
                                    icon={faQuestionCircle}
                                    size={'sm'}
                                />
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse={true}>
                                    <p>
                                        Givebutter calculates your net payout based on the
                                        transaction total minus processing fees and other applicable
                                        costs.
                                    </p>
                                    <Tooltip.Arrow inverse={true} />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    </div>
                    <p className={'text-brand-violet-primary text-sm font-medium'}>
                        {currencySymbol}
                        {maskDecimal(payout)}
                    </p>
                </div>
                {taxDeductibilitySettings && isTaxDeductibleVisible && (
                    <div className={'flex items-center justify-between gap-4'}>
                        <div className={'flex items-center gap-1'}>
                            <p className={'text-brand-violet-primary text-sm font-medium'}>
                                Tax deductible amount
                            </p>
                            <Tooltip.Root>
                                <Tooltip.Trigger>
                                    <span className="sr-only">More info</span>
                                    <FontAwesomeIcon
                                        className={'text-brand-violet-primary'}
                                        icon={faQuestionCircle}
                                        size={'sm'}
                                    />
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>
                                            This is the tax-deductible portion of the transaction,
                                            accounting for fair market value (FMV) if provided at
                                            purchase.
                                        </p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </div>
                        <p className={'text-brand-violet-primary text-sm font-medium'}>
                            {currencySymbol}
                            {maskDecimal(taxDeductibleAmount)}
                        </p>
                    </div>
                )}
            </div>
            {transaction.campaign_match && (
                <>
                    <Separator className={'-mx-4 !w-auto bg-gray-200'} />
                    <div
                        className={
                            '-m-4 flex items-center justify-between gap-4 rounded-b-xl bg-gray-100 p-4'
                        }
                    >
                        <div className={'flex items-center gap-2 font-semibold'}>
                            <span className="relative">
                                <FontAwesomeIcon
                                    icon={faSparkles}
                                    className="text-brand-butter-primary absolute -left-2 -top-0.5 rotate-[-65deg]"
                                    size="xs"
                                />
                                <FontAwesomeIcon
                                    icon={faBadgeDollar}
                                    className="text-brand-violet-primary"
                                />
                            </span>
                            <span className="text-brand-violet-primary">
                                {transaction.campaign_match.name}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            {transaction.campaign_match.is_partial_match && (
                                <Tooltip.Root>
                                    <Tooltip.Trigger>
                                        <FontAwesomeIcon
                                            className={'text-gray-400'}
                                            icon={faCircleInfo}
                                            size={'sm'}
                                        />
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content inverse={true}>
                                            <p>
                                                The donation made was greater than the remaining
                                                match amount.
                                            </p>
                                            <Tooltip.Arrow inverse={true} />
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            )}
                            <p className={'text-brand-violet-primary font-semibold'}>
                                {currencySymbol}
                                {maskDecimal(transaction.campaign_match.amount)}
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const pickOrZero = (value?: number) => value ?? 0;

export { TransactionLineItems };
